import PropTypes from 'prop-types'
import useCountUp from './useCountUp'
import { TIER_LABEL, TIER_DESC, WQ_LABEL } from '../../../lib/flow'

function Scale({ name, value, max, mean, meanLabel, label, desc, ticks }) {
  const shown = useCountUp(value)
  const pct = Math.max(0, Math.min(100, (value / max) * 100))
  return (
    <div className="scale">
      <div className="scale__top">
        <span className="scale__name">{name}</span>
        <span className="scale__val">{shown}<small>/ {max}</small></span>
      </div>
      <div className="scale__track" role="img" aria-label={`${name} ${value} out of ${max}; population mean ${mean}`}>
        <span className="scale__fill" style={{ width: `${pct}%` }} />
        <span className="scale__mean" data-label={meanLabel} style={{ left: `${(mean / max) * 100}%` }} />
        <span className="scale__marker" style={{ left: `${pct}%` }} />
      </div>
      <div className="scale__ticks" aria-hidden="true">
        {ticks.map((t) => <span key={t}>{t}</span>)}
      </div>
      <div className="scale__foot">
        <strong>{label}</strong>
        <p>{desc}</p>
      </div>
    </div>
  )
}

Scale.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  mean: PropTypes.number.isRequired,
  meanLabel: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  desc: PropTypes.node,
  ticks: PropTypes.arrayOf(PropTypes.number).isRequired,
}

function ScalesSection({ resilience, wq }) {
  const rLevel = resilience.tier?.level ?? 3
  const wLevel = wq.classification?.level ?? 4
  return (
    <section className="section" aria-labelledby="scales-h">
      <div className="section__head">
        <h2 id="scales-h">Resilience and wellness quotient</h2>
        <p className="section__desc">Two composite scores. The tick marks the population mean; your marker shows where you sit against it.</p>
      </div>
      <div className="scales">
        <Scale
          name="Resilience index"
          value={resilience.score}
          max={100}
          mean={62}
          meanLabel="mean 62"
          ticks={[0, 25, 50, 75, 100]}
          label={`${TIER_LABEL[rLevel]} · higher than ${resilience.percentile}% of similar profiles`}
          desc={TIER_DESC[rLevel]}
        />
        <Scale
          name="Wellness quotient"
          value={wq.score}
          max={200}
          mean={100}
          meanLabel="mean 100"
          ticks={[0, 50, 100, 150, 200]}
          label={`${WQ_LABEL[wLevel]} · ${wq.percentile}th percentile`}
          desc="Combines habit load, the past week, resilience and life-impact estimates on a scale centred at 100."
        />
      </div>
    </section>
  )
}

ScalesSection.propTypes = {
  resilience: PropTypes.object.isRequired,
  wq: PropTypes.object.isRequired,
}

export default ScalesSection
